/**
 * MFA Challenge Page Component
 * 
 * Handles TOTP verification for users who have MFA configured
 * but need to verify it for the current session (AAL2 elevation).
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { LoginFlow, UpdateLoginFlowBody, UiNodeInputAttributes } from '@ory/client';
import { Loader2, Shield } from 'lucide-react';
import { getIdentityClient } from '../../lib/identity-client';
import { cn } from '../../lib/utils';
import { MfaChallengePageProps } from '../../types/auth';

// =============================================================================
// MFA Challenge Page Component
// =============================================================================

export function MfaChallengePage({ 
  onSuccess, 
  onCancel, 
  returnUrl 
}: MfaChallengePageProps): JSX.Element {
  const [flow, setFlow] = useState<LoginFlow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus code input after flow initialization completes and input mounts
  useEffect(() => {
    if (!isLoading && flow && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading, flow]);

  const identityClient = getIdentityClient();

  // Initialize login flow for AAL2
  const initializeFlow = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams(window.location.search);
      const flowId = params.get('flow');
      const returnTo = params.get('return_to') || returnUrl;

      if (flowId) {
        const { data } = await identityClient.getLoginFlow({ id: flowId });
        setFlow(data);
      } else {
        // Create new login flow requesting AAL2
        const { data } = await identityClient.createBrowserLoginFlow({
          aal: 'aal2',
          returnTo: returnTo || '/',
        });
        setFlow(data);
        
        // Update URL with flow ID
        const url = new URL(window.location.href);
        url.searchParams.set('flow', data.id);
        window.history.replaceState({}, '', url.toString());
      }
    } catch (err) {
      console.error('Failed to initialize MFA challenge:', err);
      setError('Failed to initialize verification. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [identityClient, returnUrl]);

  useEffect(() => {
    initializeFlow();
  }, [initializeFlow]);

  // If flow loaded but no TOTP/lookup node, TOTP is not configured.
  // Redirect to setup (AuthGuard can't detect TOTP from public Kratos API).
  useEffect(() => {
    if (!isLoading && flow) {
      const hasTotpNode = flow.ui.nodes.some(
        n => n.group === 'totp' || n.group === 'lookup_secret'
      );
      if (!hasTotpNode) {
        window.location.href = `${window.location.origin}/auth/mfa/setup`;
      }
    }
  }, [isLoading, flow]);

  /**
   * Get CSRF token from flow
   */
  const getCsrfToken = (flowData: LoginFlow): string => {
    const csrfNode = flowData.ui.nodes.find(
      n => n.attributes.node_type === 'input' && 
           (n.attributes as UiNodeInputAttributes).name === 'csrf_token'
    );
    
    if (csrfNode && csrfNode.attributes.node_type === 'input') {
      return (csrfNode.attributes as UiNodeInputAttributes).value as string || '';
    }
    
    return '';
  };

  // Handle code verification
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flow || !code) return;

    // Validate code length
    if (!useRecoveryCode && code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const csrfToken = getCsrfToken(flow);
      
      const body: UpdateLoginFlowBody = useRecoveryCode
        ? {
            method: 'lookup_secret',
            lookup_secret: code,
            csrf_token: csrfToken,
          }
        : {
            method: 'totp',
            totp_code: code,
            csrf_token: csrfToken,
          };

      await identityClient.updateLoginFlow({
        flow: flow.id,
        updateLoginFlowBody: body,
      });

      // Verification successful
      onSuccess();
    } catch (err: unknown) {
      console.error('MFA verification failed:', err);
      
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { data?: LoginFlow } }).response;
        if (response?.data) {
          setFlow(response.data);
          
          // Extract error message
          const errorMsg = response.data.ui.messages?.find(m => m.type === 'error');
          if (errorMsg) {
            setError(errorMsg.text);
          } else {
            setError('Verification failed. Please check the code and try again.');
          }
        }
      } else {
        setError('Verification failed. Please check the code and try again.');
      }
      
      // Clear code on error
      setCode('');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle recovery code mode
  const handleToggleRecoveryCode = () => {
    setUseRecoveryCode(!useRecoveryCode);
    setCode('');
    setError(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Two-Factor Authentication
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {useRecoveryCode 
              ? 'Enter one of your recovery codes'
              : 'Enter the code from your authenticator app'
            }
          </p>
        </div>

        {/* MFA Challenge Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
          {error && (
            <div className="mb-6 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleVerify} className="space-y-6">
            {/* Code input */}
            <div>
              <label 
                htmlFor="code"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
              >
                {useRecoveryCode ? 'Recovery Code' : 'Authentication Code'}
              </label>
              <input
                ref={inputRef}
                id="code"
                type="text"
                inputMode={useRecoveryCode ? 'text' : 'numeric'}
                pattern={useRecoveryCode ? undefined : '[0-9]{6}'}
                maxLength={useRecoveryCode ? 20 : 6}
                autoComplete={useRecoveryCode ? 'off' : 'one-time-code'}
                autoFocus
                placeholder={useRecoveryCode ? 'Enter recovery code' : '000000'}
                value={code}
                onChange={(e) => {
                  const value = useRecoveryCode 
                    ? e.target.value 
                    : e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(value);
                }}
                disabled={isSubmitting}
                className={cn(
                  'w-full px-4 py-4 rounded-lg border text-center font-mono',
                  useRecoveryCode ? 'text-lg tracking-wide' : 'text-3xl tracking-[0.5em]',
                  'bg-white dark:bg-gray-900',
                  'text-gray-900 dark:text-gray-100',
                  'placeholder:text-gray-300 dark:placeholder:text-gray-600',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                  'border-gray-300 dark:border-gray-600',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              />
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting || (!useRecoveryCode && code.length !== 6) || (useRecoveryCode && !code)}
              className={cn(
                'w-full py-3 px-4 rounded-lg font-medium transition-colors',
                'bg-blue-600 text-white hover:bg-blue-700',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying...
                </span>
              ) : (
                'Verify'
              )}
            </button>
          </form>

          {/* Toggle recovery code option */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              {useRecoveryCode ? (
                <>
                  Have your authenticator app?{' '}
                  <button
                    onClick={handleToggleRecoveryCode}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Use authenticator code
                  </button>
                </>
              ) : (
                <>
                  Lost access to your authenticator?{' '}
                  <button
                    onClick={handleToggleRecoveryCode}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Use a recovery code
                  </button>
                </>
              )}
            </p>
          </div>
        </div>

        {/* Back to login / Cancel */}
        <div className="mt-6 text-center">
          {onCancel ? (
            <button
              onClick={onCancel}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            >
              ← Cancel
            </button>
          ) : (
            <a
              href="/auth/login"
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            >
              ← Back to login
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default MfaChallengePage;
